/**
 * jiraSemanticApi — thin client for the integrations engine's semantic Jira
 * read (engines/integrations-engine/src/routes/jira.ts `/jira/import-semantic`).
 * Beside linearSemanticApi / notionSemanticApi; keeps integrationsApi.ts
 * untouched for the lint gate. The flat `/jira/issues` contract is unchanged —
 * this additive read also carries issue links and the parent chain.
 */

import { INTEGRATIONS_ENGINE_URL } from '../engineUrls';

/** A Jira issue link, collapsed by directional cue name + the issues it points at. */
export interface JiraLinkRead {
  /** 'blocks' | 'blocked_by' | 'related' | 'duplicate' — the cue that resolves to a canonical type. */
  type: string;
  targetExternalIds: string[];
}

export interface JiraIssueRead {
  externalId: string;
  externalUrl?: string;
  title: string;
  description?: string;
  /** The parent (epic/subtask parent) issue's external id. */
  parentExternalId?: string;
  relations: JiraLinkRead[];
}

export interface JiraProjectRead {
  project: { id: string; name: string; url?: string };
  issues: JiraIssueRead[];
}

async function engineRequest<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${INTEGRATIONS_ENGINE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const payload = (await response.json().catch(() => ({}))) as T & { success?: boolean; error?: string; details?: string };
  if (!response.ok || payload.success === false) {
    throw new Error(payload.error ?? payload.details ?? `Integrations engine error ${response.status}`);
  }
  return payload;
}

/**
 * Read a Jira project's issues as a semantic dialect surface: issues with their
 * links and parent chain — the data the flat import drops.
 */
export async function jiraImportSemantic(
  workspaceId: string,
  projectKey: string,
): Promise<JiraProjectRead> {
  return engineRequest<JiraProjectRead>('POST', '/jira/import-semantic', { workspaceId, projectKey });
}

/** One Jira issue's sizing and duration, as `/jira/estimates` returns it. */
export interface JiraIssueEstimateRead {
  key: string;
  summary: string;
  issueType: string | null;
  status: string | null;
  storyPoints: number | null;
  originalEstimateSeconds: number | null;
  timeSpentSeconds: number | null;
  createdAt: string | null;
  resolvedAt: string | null;
  cycleDays: number | null;
  linked: boolean;
}

/**
 * Estimates and durations for named issues and/or issues matching a search in
 * a project — the Value Estimator's connected-history evidence (PRD §5).
 */
export async function readJiraEstimates(params: {
  workspaceId: string;
  keys?: string[];
  projectKey?: string;
  search?: string;
  maxResults?: number;
}): Promise<JiraIssueEstimateRead[]> {
  const payload = await engineRequest<{ issues?: JiraIssueEstimateRead[] }>('POST', '/jira/estimates', params);
  return payload.issues ?? [];
}
